/**
 * IndexedDB offline storage for RSSio using Dexie.js
 */

import Dexie, { type Table } from "dexie";
import { Capacitor } from "@capacitor/core";

export const DB_NAME = "rssio-db";
export const STORE_NAME = "articles";

export interface OfflineArticleRecord {
  id: string;
  guid?: string;
  feedId?: string;
  title?: string;
  author?: string;
  pubDate?: string;
  link?: string | string[] | { href?: string; $?: { href?: string } };
  content: string;
  feedTitle?: string;
  imageUrl?: string;
  videoFilePath?: string;
  videoData?: ArrayBuffer | null;
  videoBlob?: Blob;
  videoMimeType?: string;
  source?: string | null;
  isFullContent?: boolean;
  savedAt?: number;
  updatedAt?: number;
  deletedAt?: number;
  deviceId?: string;
}

export interface OfflineArticleAssetIndex {
  [articleId: string]: {
    imageUrl?: string;
    videoUrl?: string;
    videoFilePath?: string;
    hasVideo?: boolean;
  };
}

/**
 * Dexie.js database for RSSio offline storage
 */
class RssioDatabase extends Dexie {
  articles!: Table<OfflineArticleRecord, string>;

  constructor() {
    super(DB_NAME);
    this.version(1).stores({
      articles: "id, feedId, savedAt, updatedAt",
      subscriptions: "id, url, category, updatedAt",
      categories: "id, name, updatedAt",
      videoPositions: "id, updatedAt",
      syncState: "id",
    });
    // Remove the old generic-sync stores on upgraded installations. The
    // articles store is intentionally retained for offline reading and media.
    this.version(2).stores({
      articles: "id, feedId, savedAt, updatedAt",
      subscriptions: null,
      categories: null,
      videoPositions: null,
      syncState: null,
    });
  }
}

// Singleton database instance
export const db = new RssioDatabase();

// Initialize database (Dexie opens automatically and runs pending migrations)
export async function initDB(): Promise<void> {
  // Dexie opens the database automatically on first use
  // This function exists for backward compatibility
  await db.open();
}

// Save article for offline reading
export async function saveArticleOffline(
  articleId: string,
  article: Partial<OfflineArticleRecord> | OfflineArticleRecord,
  options: { preserveExistingVideo?: boolean } = {},
): Promise<void> {
  const articleForStorage = { ...article };
  const preserveExistingVideo = options.preserveExistingVideo !== false;

  let existingArticle: OfflineArticleRecord | undefined;
  try {
    existingArticle = await db.articles.get(articleId);
  } catch {
    existingArticle = undefined;
  }

  const hasIncomingVideoFile =
    typeof articleForStorage.videoFilePath === "string" && articleForStorage.videoFilePath;
  const hasIncomingVideoBlob = articleForStorage.videoBlob instanceof Blob;
  const hasIncomingVideoData = articleForStorage.videoData instanceof ArrayBuffer;

  if (
    preserveExistingVideo &&
    !hasIncomingVideoFile &&
    !hasIncomingVideoBlob &&
    !hasIncomingVideoData &&
    existingArticle
  ) {
    if (typeof existingArticle.videoFilePath === "string" && existingArticle.videoFilePath) {
      articleForStorage.videoFilePath = existingArticle.videoFilePath;
      articleForStorage.videoMimeType =
        articleForStorage.videoMimeType || existingArticle.videoMimeType || "video/mp4";
    } else if (existingArticle.videoBlob instanceof Blob) {
      articleForStorage.videoBlob = existingArticle.videoBlob;
      articleForStorage.videoMimeType =
        articleForStorage.videoMimeType || existingArticle.videoMimeType || "video/mp4";
    }
  }

  if (typeof articleForStorage.videoFilePath === "string" && articleForStorage.videoFilePath) {
    articleForStorage.videoBlob = undefined;
    articleForStorage.videoData = null;
  }

  if (articleForStorage.videoBlob instanceof Blob) {
    try {
      articleForStorage.videoData = await articleForStorage.videoBlob.arrayBuffer();
    } catch {
      articleForStorage.videoData = null;
    }

    articleForStorage.videoBlob = undefined;
    articleForStorage.videoMimeType = articleForStorage.videoMimeType || "video/mp4";
  }

  await db.articles.put({
    id: articleId,
    content: articleForStorage.content || "",
    ...articleForStorage,
    savedAt: Date.now(),
  });
}

// Get offline article
export async function getOfflineArticle(articleId: string): Promise<OfflineArticleRecord | null> {
  const result = await db.articles.get(articleId);
  if (!result) {
    return null;
  }

  if (typeof result.videoFilePath === "string" && result.videoFilePath) {
    return result;
  }

  // Convert videoData (ArrayBuffer) back to Blob
  if (result.videoData instanceof ArrayBuffer) {
    const blob = new Blob([result.videoData], { type: result.videoMimeType || "video/mp4" });
    return {
      ...result,
      videoBlob: blob,
      videoData: null, // Remove the ArrayBuffer
    };
  }

  return result;
}

// Remove offline article
export async function removeOfflineArticle(articleId: string): Promise<void> {
  await db.articles.delete(articleId);
}

// Bulk remove offline articles in a single atomic transaction
export async function bulkDeleteOfflineArticles(articleIds: string[]): Promise<void> {
  if (!articleIds || articleIds.length === 0) return;
  await db.articles.bulkDelete(articleIds);
}

// Bulk save offline articles in a single atomic transaction
export async function bulkSaveArticlesOffline(records: OfflineArticleRecord[]): Promise<void> {
  if (!records || records.length === 0) return;
  await db.articles.bulkPut(records);
}

// Get all offline articles
export async function getAllOfflineArticles(): Promise<OfflineArticleRecord[]> {
  const results = await db.articles.toArray();
  return results || [];
}

// Get all offline article IDs (fast index-only scan without loading full article bodies)
export async function getAllOfflineArticleIds(): Promise<string[]> {
  const keys = await db.articles.toCollection().primaryKeys();
  return keys.map((k) => String(k));
}

export async function getOfflineArticleAssetIndex(): Promise<OfflineArticleAssetIndex> {
  const allArticles = await db.articles.toArray();
  const index: OfflineArticleAssetIndex = {};

  for (const value of allArticles) {
    if (value?.id) {
      const hasVideoFile = typeof value.videoFilePath === "string" && value.videoFilePath.length > 0;
      const hasVideoData = value.videoData instanceof ArrayBuffer;
      const hasVideoBlob = value.videoBlob instanceof Blob;
      const hasVideo = hasVideoFile || hasVideoBlob || hasVideoData;
      const videoBlob = hasVideoBlob
        ? value.videoBlob!
        : hasVideoData
          ? new Blob([value.videoData!], {
            type: value.videoMimeType || "video/mp4",
          })
          : null;
      index[value.id] = {
        imageUrl: typeof value.imageUrl === "string" ? value.imageUrl : undefined,
        videoUrl: hasVideoFile
          ? Capacitor.convertFileSrc(value.videoFilePath!)
          : videoBlob
            ? URL.createObjectURL(videoBlob)
            : undefined,
        videoFilePath: hasVideoFile ? value.videoFilePath : undefined,
        hasVideo,
      };
    }
  }

  return index;
}
